import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  currentMemberIdToken,
  deleteSavedAddress,
  fetchSavedAddresses,
  saveSavedAddress,
  type SavedAddress,
} from '../lib/api/memberApi'
import { ThaiAddressInput } from '../components/form/ThaiAddressInput'
import { addressPartsOfText, isAddressTextReady } from '../lib/thaiAddressText'
import { readOpenIntent } from './memberJoin'

/** เพิ่มใหม่ได้ 2 ชุด (ที่อยู่หลัก = ข้อมูลสมาชิก) — ตรงกับ MAX_NEW_ADDRESSES ฝั่ง CF */
export const MAX_BACKUP_ADDRESSES = 2

type Draft = { id?: string; label: string; recipientName: string; phone: string; address: string }
const EMPTY: Draft = { label: '', recipientName: '', phone: '', address: '' }

function formatPhone(phone: string): string {
  const d = String(phone || '').replace(/\D/g, '')
  return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : d
}

/**
 * ที่อยู่จัดส่งสำรอง (สมุดที่อยู่ memberAddresses) — เลือกใช้ได้จากปุ่ม "เลือกที่อยู่ของฉัน" หน้าออร์เดอร์
 * ลิงก์ ?open=backup (จากแผ่นเลือกที่อยู่) → เลื่อนมาที่ส่วนนี้ + เปิดฟอร์มเพิ่มเมื่อยังว่าง
 */
export function BackupAddresses({ defaults }: { defaults?: { recipientName?: string; phone?: string } }) {
  const [list, setList] = useState<SavedAddress[] | null>(null)
  const [error, setError] = useState('')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')
  const [confirmDelete, setConfirmDelete] = useState('')
  const sectionRef = useRef<HTMLElement>(null)
  const intentRef = useRef(readOpenIntent(window.location.search) === 'backup')

  type AddressCall = (token: string) => Promise<{ data: { ok: boolean; addresses?: SavedAddress[]; message?: string } }>
  const run = async (fn: AddressCall) => {
    const token = await currentMemberIdToken()
    if (!token) throw new Error('กรุณาเปิดจาก LINE อีกครั้ง')
    const response = await fn(token)
    if (!response.data.ok) throw new Error(response.data.message || 'ทำรายการไม่สำเร็จ')
    setList(response.data.addresses || [])
  }

  useEffect(() => {
    let active = true
    void run((token) => fetchSavedAddresses(token)).catch((cause) => {
      if (active) setError(cause instanceof Error ? cause.message : 'โหลดที่อยู่ไม่สำเร็จ')
    })
    return () => { active = false }
  }, [])

  useEffect(() => {
    if (!intentRef.current || list === null) return
    intentRef.current = false
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    if (list.length < MAX_BACKUP_ADDRESSES) setDraft({ ...EMPTY, recipientName: defaults?.recipientName || '', phone: defaults?.phone || '' })
  }, [list, defaults])

  useEffect(() => {
    if (!draft) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setDraft(null) }
    document.addEventListener('keydown', onKey)
    return () => { document.body.style.overflow = previous; document.removeEventListener('keydown', onKey) }
  }, [draft])

  const save = async () => {
    if (!draft) return
    if (!draft.label.trim() || !draft.recipientName.trim() || !draft.phone.trim()) { setFormError('กรอกชื่อเรียก ชื่อผู้รับ และเบอร์โทรให้ครบ'); return }
    if (!isAddressTextReady(draft.address)) { setFormError('กรุณากรอกที่อยู่ให้ครบ (บ้านเลขที่ รหัสไปรษณีย์ จังหวัด อำเภอ ตำบล)'); return }
    setSaving(true)
    setFormError('')
    try {
      await run((token) => saveSavedAddress(token, { ...draft, addressParts: addressPartsOfText(draft.address) }))
      setDraft(null)
    } catch (cause) {
      setFormError(cause instanceof Error ? cause.message : 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (id: string) => {
    setConfirmDelete('')
    try { await run((token) => deleteSavedAddress(token, id)) } catch (cause) { setError(cause instanceof Error ? cause.message : 'ลบไม่สำเร็จ') }
  }

  const items = list || []
  const canAdd = list !== null && items.length < MAX_BACKUP_ADDRESSES
  return <>
    <section ref={sectionRef} className="mo-glass mo-backup" aria-label="ที่อยู่จัดส่งสำรอง">
      <div className="mo-backup-head">
        <b>ที่อยู่จัดส่งสำรอง</b>
        <small>{list === null ? 'กำลังโหลด...' : `${items.length}/${MAX_BACKUP_ADDRESSES} ชุด · เลือกใช้ได้ตอนกรอกออร์เดอร์`}</small>
      </div>
      {error && <p className="mo-address-error" role="alert">{error}</p>}
      {items.map((item) => <div key={item.id} className="mo-backup-item">
        <div className="mo-backup-copy">
          <b>{item.label}</b>
          <small>{item.recipientName} · {formatPhone(item.phone)}</small>
          <span>{item.address}</span>
        </div>
        {confirmDelete === item.id
          ? <div className="mo-backup-actions" role="group" aria-label="ยืนยันการลบ">
            <span>ลบที่อยู่นี้?</span>
            <button type="button" className="is-danger" onClick={() => void remove(item.id)}>ลบ</button>
            <button type="button" onClick={() => setConfirmDelete('')}>ไม่ลบ</button>
          </div>
          : <div className="mo-backup-actions">
            <button type="button" onClick={() => { setFormError(''); setDraft({ id: item.id, label: item.label, recipientName: item.recipientName, phone: item.phone, address: item.address }) }}>แก้ไข</button>
            <button type="button" className="is-danger" onClick={() => setConfirmDelete(item.id)}>ลบ</button>
          </div>}
      </div>)}
      {canAdd && <button type="button" className="mo-backup-add" onClick={() => { setFormError(''); setDraft({ ...EMPTY, recipientName: defaults?.recipientName || '', phone: defaults?.phone || '' }) }}>
        ＋ เพิ่มที่อยู่สำรอง <small>(เหลือ {MAX_BACKUP_ADDRESSES - items.length} ชุด)</small>
      </button>}
    </section>
    {draft && createPortal(<div className="mo-address-backdrop" role="presentation" onClick={() => setDraft(null)}>
      <section className="mo-address-sheet" role="dialog" aria-modal="true" aria-label={draft.id ? 'แก้ไขที่อยู่สำรอง' : 'เพิ่มที่อยู่สำรอง'} onClick={(event) => event.stopPropagation()}>
        <div className="mo-address-handle" />
        <div className="mo-address-head"><h2>{draft.id ? 'แก้ไขที่อยู่สำรอง' : 'เพิ่มที่อยู่สำรอง'}</h2><button type="button" onClick={() => setDraft(null)} aria-label="ปิด">×</button></div>
        {formError && <p className="mo-address-error" role="alert">{formError}</p>}
        <form className="mo-address-form" onSubmit={(event) => { event.preventDefault(); void save() }}>
          <label>ชื่อเรียก<input required value={draft.label} maxLength={30} placeholder="เช่น บ้านแม่ / ที่ทำงาน" onChange={(event) => setDraft({ ...draft, label: event.target.value })} /></label>
          <label>ชื่อผู้รับ<input required autoComplete="name" value={draft.recipientName} maxLength={100} onChange={(event) => setDraft({ ...draft, recipientName: event.target.value })} /></label>
          <label>เบอร์โทรผู้รับ<input required autoComplete="tel" type="tel" inputMode="tel" value={draft.phone} maxLength={15} onChange={(event) => setDraft({ ...draft, phone: event.target.value })} /></label>
          <fieldset className="mo-address-fieldset">
            <ThaiAddressInput id="memberBackupAddressLine" variant="sheet" title="ที่อยู่จัดส่ง" value={draft.address}
              onChange={(text) => setDraft((prev) => (prev ? { ...prev, address: text } : prev))}
              lineProps={{ required: true, autoComplete: 'street-address' }} />
          </fieldset>
          <button type="submit" className="mo-address-primary" disabled={saving}>{saving ? 'กำลังบันทึก...' : 'บันทึกที่อยู่'}</button>
        </form>
      </section>
    </div>, document.body)}
  </>
}
